<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import useVuelidate from '@vuelidate/core'
import { email, required } from '@vuelidate/validators'
import AuthLayout from '@/modules/auth/layouts/auth-layout.vue'
import { VButton, VTextField } from '@/modules/shared/components'
import { useGroupStore } from '../../stores/group-store'
import FormError from '@/modules/auth/components/form-error.vue'

const router = useRouter()
const baseUrl = ref<string>(window.location.origin)
const loading = ref(false)
const useGroup = useGroupStore()
///////////////////////////////////////////////////////////////////////////////
// Group Verification
///////////////////////////////////////////////////////////////////////////////

const form = ref({
  email: null,
  group: null,
})
const rules = {
  email: { required, email },
  group: { required },
}
const v$ = useVuelidate(rules, form, { $lazy: true })
const requestGroupVerification = async () => {
  const valid = await v$.value.$validate()
  if (!valid) return

  loading.value = true

  const payload = {
    group: form.value.group,
    email: form.value.email,
  }
  useGroup.clearErrorMessage()
  await useGroup.createGroup(payload)
  if (!useGroup.errorMessage) {
    router.push({ name: 'email-verification-instructions', params: { slug: 'group' } })
  }

  loading.value = false
}
</script>

<template>
  <AuthLayout>
    <template v-slot:icon>
      <svg width="72" height="96" viewBox="0 0 72 96" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M63 39H9C5.68629 39 3 41.6863 3 45V87C3 90.3137 5.68629 93 9 93H63C66.3137 93 69 90.3137 69 87V45C69 41.6863 66.3137 39 63 39Z"
          fill="#111827"
          stroke="#111827"
          stroke-width="6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M15 39V24C15 12.4 24.4 3 36 3C47.56 3 57 12.4 57 24V39"
          stroke="#111827"
          stroke-width="6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path d="M36 60V72" stroke="white" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </template>

    <div class="w-full w-full px-6 sm:px-12">
      <header>
        <h1 class="text-center text-4xl font-bold">Welcome!</h1>
      </header>
      <div class="mt-6 flex items-center gap-3 sm:mt-20">
        <div class="h-[1px] w-full bg-black/10"></div>
        <h3 class="whitespace-nowrap px-3 text-center text-xl">Register a new account</h3>
        <div class="h-[1px] w-full bg-black/10"></div>
      </div>
      <template v-if="true">
        <main class="sm:mt-18 mt-12">
          <FormError :error="useGroup.errorMessage" v-show="useGroup.errorMessage !== null" />
          <form class="mb-12" @submit.prevent="requestGroupVerification">
            <div class="space-y-5">
              <VTextField v-model="form.group" label="Firm Name" placeholder="Firm Name" property="group" :v$="v$" />
            </div>
            <div class="mt-5 space-y-5">
              <VTextField v-model="form.email" label="Email" placeholder="Email" property="email" :v$="v$" />
            </div>
          </form>
          <VButton
            class="mt-16 w-full font-semibold"
            :loading="loading"
            size="xl"
            variant="v-blue"
            @click="requestGroupVerification"
          >
            Verify
          </VButton>
        </main>
      </template>

      <div class="mb-8 mt-12 flex items-center gap-3">
        <div class="h-[1px] w-full bg-black/10"></div>
        <div class="">OR</div>
        <div class="h-[1px] w-full bg-black/10"></div>
      </div>
      <a :href="`${baseUrl}`">
        <VButton variant="primary" class="w-full font-semibold" size="xl">Login</VButton>
      </a>
    </div>
  </AuthLayout>
</template>
